import {match} from 'ts-pattern';
import {z} from 'zod';

import {isNil} from 'ramda';

import type {GridPdfExportContext} from 'features/eag-grid';
import {PdfExportError} from 'features/pdf-export';

import {RECEIVE_NOTE_PDF_COLUMNS} from '../constants/RECEIVE_NOTE_PDF_COLUMNS';

const EMPTY_VALUE = '-';
const labelSchema = z.object({label: z.string().nullable()});
const textCellSchema = z.object({value: z.union([z.string(), labelSchema]).nullable()});
const decimalSchema = z.union([
  z.number().finite(),
  z
    .string()
    .trim()
    .min(1)
    .refine((value) => Number.isFinite(Number(value)) && !/^[+-]?0[xbo]/iu.test(value))
    .transform(Number)
    .pipe(z.number().finite()),
]);
const rowSchema = z.object({
  receiveNote: textCellSchema,
  branchName: textCellSchema,
  receiveNoteType: textCellSchema,
  state: textCellSchema,
  warehouse: textCellSchema,
  supplier: textCellSchema,
  deliveryNoteNo: textCellSchema,
  hasFastTrack: z.object({value: z.boolean().nullable()}),
  purchasePrice: z.object({
    value: z
      .object({amount: decimalSchema.nullable(), currency: z.string().regex(/^[A-Z]{3}$/u)})
      .nullable(),
  }),
});

type PdfFormatters = {
  currency: (amount: number, currency: string) => string | null;
  yes: string;
  no: string;
};

/** Project the nine verified receive-note grid fields without enriching or converting values. */
export function createReceiveNotePdfData(context: GridPdfExportContext, format: PdfFormatters) {
  if (context.rows.length === 0) {
    throw new PdfExportError('general.notifications.noItemsSelected');
  }
  const titles = new Map(context.columns.map((column) => [column.key, column.title]));
  return context.rows.map((row) => {
    try {
      const cells = rowSchema.parse(row.cells);
      const field = (key: (typeof RECEIVE_NOTE_PDF_COLUMNS)[number], value: string) => ({
        key,
        label: z.string().trim().min(1).parse(titles.get(key)),
        value: value.trim().length === 0 ? EMPTY_VALUE : value,
      });
      const textField = (key: Exclude<keyof typeof cells, 'purchasePrice' | 'hasFastTrack'>) => {
        const value = cells[key].value;
        const text = typeof value === 'string' ? value : value?.label;
        return field(key, text ?? EMPTY_VALUE);
      };
      const price = cells.purchasePrice.value;
      const priceText =
        isNil(price) || isNil(price.amount)
          ? EMPTY_VALUE
          : z.string().trim().min(1).parse(format.currency(price.amount, price.currency));
      const fastTrack = cells.hasFastTrack.value;
      return {
        id: row.id,
        receiveNote: textField('receiveNote'),
        branchName: textField('branchName'),
        receiveNoteType: textField('receiveNoteType'),
        state: textField('state'),
        warehouse: textField('warehouse'),
        supplier: textField('supplier'),
        deliveryNoteNo: textField('deliveryNoteNo'),
        hasFastTrack: field(
          'hasFastTrack',
          match(fastTrack)
            .with(true, () => format.yes)
            .with(false, () => format.no)
            .otherwise(() => EMPTY_VALUE)
        ),
        purchasePrice: field('purchasePrice', priceText),
      };
    } catch (cause) {
      throw new PdfExportError(
        'module.reportDocument.labels.dataUnavailable',
        false,
        'entity.integration.labels.pleaseRefresh',
        {cause}
      );
    }
  });
}
